import ApartmentRounded from "@mui/icons-material/ApartmentRounded";
import ContactPageRounded from "@mui/icons-material/ContactPageRounded";
import EngineeringRoundedIcon from "@mui/icons-material/EngineeringRounded";
import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import HourglassDisabledRounded from "@mui/icons-material/HourglassDisabledRounded";
import LocalPoliceRounded from "@mui/icons-material/LocalPoliceRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import SupervisorAccountRounded from "@mui/icons-material/SupervisorAccountRounded";
import WarningRounded from "@mui/icons-material/WarningRounded";
import WhatshotRounded from "@mui/icons-material/WhatshotRounded";
import { UserSchema } from "@tsTypes/next-auth";
import { GradeBoundary, Subject } from "@tsTypes/subject-schema";
import {
  OverallResourceTotalsSchema,
  SimplifiedOverallResourceTotalsSchema,
} from "@tsTypes/user-metadata-schema";
import { strapiData } from "../strapi/strapiApi";
import { isoStringToUnix } from "../stripe/formatters";
import { metaCacheTags } from "./cacheTags";
import {
  buildSubjectTestQuery,
  fetchAllBlogArticles,
  getSubjects,
} from "./dataFetching";
import { roundTo2DP } from "./helpers";
import { buildTopicSlug, parseSubjectUrl, titleToUrl } from "./routing";

// **************************************************************************************** STATIC PARAMS
export async function allSubjectsParams(subjectsToDisable?: string[]) {
  const subjects = await getSubjects();
  const links: { subject: string }[] = [];
  subjects.map((subj) => {
    if (subjectsToDisable?.includes(subj.Name.toLowerCase())) {
      return;
    }
    links.push({ subject: titleToUrl(subj.Name) });
  });
  return links;
}

export async function allBlogArticleSlugParams() {
  const articles = await fetchAllBlogArticles();
  return articles.map((a) => ({ slug: a.slug }));
}

export async function allTopicStaticParams(subjectsToDisable?: string[]) {
  const subjects = await getSubjects();
  const links: { subject: string; topic: string }[] = [];
  subjects.map((subj) => {
    // for certain subjects don't render topics for certain things
    if (subjectsToDisable?.includes(subj.Name.toLowerCase())) {
      return;
    }
    links.push(
      ...subj.RelatedTopics.map((topic) => {
        const subjSlug = titleToUrl(subj.Name);
        return {
          subject: subjSlug,
          topic: buildTopicSlug(
            subj.Name,
            topic.TopicNumber.toString(),
            topic.TopicTitle
          ),
        };
      })
    );
  });
  return links;
}

export async function allSubTopicStaticParams(subjectsToDisable?: string[]) {
  const subjects = await getSubjects();
  const links: { subject: string; topic: string; subTopic: string }[] = [];
  subjects.map((subj) =>
    subj.RelatedTopics.map((topic) => {
      const subjSlug = titleToUrl(subj.Name);
      // for certain subjects don't render subtopics for certain things
      if (subjectsToDisable?.includes(subj.Name.toLowerCase())) {
        return;
      }
      const data: { subject: string; topic: string; subTopic: string }[] = [];
      data.push(
        ...topic.RelatedSubTopics.map((subTopic) => {
          return {
            subject: subjSlug,
            topic: buildTopicSlug(
              subj.Name,
              topic.TopicNumber.toString(),
              topic.TopicTitle
            ),
            subTopic: titleToUrl(subTopic.SubTopicTitle),
          };
        })
      );
      links.push(...data);
    })
  );
  return links;
}

export async function allTestStaticParams() {
  const subjects: Subject[] = await strapiData(
    process.env.STRAPI_API_TOKEN!,
    "subjects",
    buildSubjectTestQuery(),
    undefined,
    undefined,
    undefined,
    undefined,
    metaCacheTags(),
  );
  const links: { subject: string; topic: string; testID: string }[] = [];
  subjects.map((subj) =>
    subj.RelatedTopics.map((topic) => {
      const subjSlug = titleToUrl(subj.Name);
      const data: { subject: string; topic: string; testID: string }[] = [];
      data.push(
        ...topic.RelatedTopicTests!.map((test) => {
          return {
            subject: subjSlug,
            topic: buildTopicSlug(
              subj.Name,
              topic.TopicNumber.toString(),
              topic.TopicTitle
            ),
            testID: test.documentId,
          };
        })
      );
      links.push(...data);
    })
  );
  return links;
}
// *************************************************************************************** SUBJECT PAGE LINKS META
export async function getResourceTotals() {
  // data fetch
  const data: OverallResourceTotalsSchema = await strapiData(
    process.env.STRAPI_API_TOKEN as string,
    "get-subject-totals",
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    metaCacheTags(),
  );
  return data;
}

export async function getSimplifiedResourceTotals() {
  // data fetch
  const data: SimplifiedOverallResourceTotalsSchema = await strapiData(
    process.env.STRAPI_API_TOKEN as string,
    "get-simplified-subject-totals",
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    metaCacheTags(),
  );
  return data;
}

// **************************************************************************************** USER META
export function getExamPeriods() {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
  const endYear = currentYear + 1;
  const examPeriods: { label: string; value: string }[] = [];
  for (let year = currentYear; year <= endYear; year++) {
    if (year === currentYear) {
      // Only add future exam periods for the current year
      if (currentMonth <= 5) {
        examPeriods.push({ label: `May ${year}`, value: `${year}-05-01` }); // JavaScript months are 0-indexed so May is 4
      }
      if (currentMonth <= 11) {
        examPeriods.push({
          label: `Nov ${year}`,
          value: `${year}-11-01`,
        }); // JavaScript months are 0-indexed so Nov is 10
      }
    } else {
      // Add both May and November for future years
      examPeriods.push(
        { label: `May ${year}`, value: `${year}-05-01` },
        {
          label: `Nov ${year}`,
          value: `${year}-11-01`,
        }
      );
    }
  }
  return examPeriods;
}

export function getDaysToExpiry(endDate: string) {
  const daysLeft = Math.floor(
    (isoStringToUnix(endDate) - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysLeft < 365) {
    return `${daysLeft} days`;
  }
  const years = Math.floor(daysLeft / 365);
  const days = daysLeft % 365;
  return `${years}y ${days} day${days > 1 ? "s" : ""}`;
}

export function getMembershipIcon(
  role: UserSchema["role"] | undefined,
  userSubscriptionMeta: UserSchema["subscriptionMeta"]
) {
  let color;
  let name:
    | "Super Admin"
    | "Admin"
    | "Content Creator"
    | "Co-ordinator"
    | "Student"
    | "Teacher"
    | "Member"
    | "Trial"
    | "Expired"
    | "Suspended"
    | "Lifetime"
    | "Basic";
  let Icon;
  // if any special access account
  if (role === "Super Admin") {
    color = "dark";
    name = "Super Admin";
    Icon = LocalPoliceRounded;
  } else if (role === "Admin") {
    color = "dark";
    name = "Admin";
    Icon = LocalPoliceRounded;
  } else if (role === "Content Creator") {
    color = "correct";
    name = "Content Creator";
    Icon = EngineeringRoundedIcon;
  } else if (role === "Co-ordinator") {
    color = "lightblue";
    name = "Co-ordinator";
    if (userSubscriptionMeta?.status === "suspended") {
      Icon = WarningRounded;
    } else {
      Icon = SupervisorAccountRounded;
    }
  } else if (role === "Student") {
    color = "lightblue";
    name = "Student";
    Icon = ApartmentRounded;
  } else if (role === "Teacher") {
    color = "lightblue";
    name = "Teacher";
    Icon = SchoolRounded;
  } // if any regular account
  else {
    switch (userSubscriptionMeta?.status) {
      case "active":
        color = "gold";
        name = "Member";
        Icon = WhatshotRounded;
        break;
      case "trialing":
        name = "Trial";
        color = "darkblue";
        Icon = HourglassBottomRounded;
        break;
      case "expired":
        color = "incorrect";
        name = "Expired";
        Icon = HourglassDisabledRounded;
        break;
      case "suspended":
        color = "incorrect";
        name = "Suspended";
        Icon = WarningRounded;
        break;
      // when undefined, user has no subscription
      default:
        color = "dark";
        name = "Basic";
        Icon = ContactPageRounded;
    }
  }
  return { Icon, color, name };
}
// **************************************************************************************** RESOURCE META
export async function findCurrentSubject(
  subjectName: string,
  withSyllabusPoints?: true
) {
  const subjects = await getSubjects(withSyllabusPoints);
  const currentSubject = subjects.find(
    (subj) => subj.Name === parseSubjectUrl(subjectName)
  )!;
  return currentSubject;
}

// **************************************************************************************** PROGRESS META
export function convertPercentageToGrade(
  percentage: number,
  gradeBoundaries: GradeBoundary
) {
  const { grade1, grade2, grade3, grade4, grade5, grade6, grade7 } =
    gradeBoundaries;

  if (percentage < grade1) {
    return 0;
  } else if (percentage >= grade1 && percentage < grade2) {
    return 1;
  } else if (percentage >= grade2 && percentage < grade3) {
    return 2;
  } else if (percentage >= grade3 && percentage < grade4) {
    return 3;
  } else if (percentage >= grade4 && percentage < grade5) {
    return 4;
  } else if (percentage >= grade5 && percentage < grade6) {
    return 5;
  } else if (percentage >= grade6 && percentage < grade7) {
    return 6;
  } else {
    return 7;
  }
}

export function buildMonthlyData(dates: number[], performances?: number[]) {
  if (dates.length === 0) {
    return;
  }
  let yearsIncluded: number[] = [];
  let yearStructure: {
    [monthYear: string]: { [weekNumber: number]: number | null };
  } = {};
  let attemptsYearStructure: {
    [monthYear: string]: { [weekNumber: number]: number };
  } = {};
  // for all data points
  dates.map((d, indx) => {
    const dateObj = new Date(d);
    const year = dateObj.getFullYear();
    // create year blank data if not yet done so
    if (!yearsIncluded.includes(year)) {
      yearsIncluded.push(year);
      for (let i = 1; i <= 12; i++) {
        const monthNumber = i < 10 ? `0${i}` : i;
        const monthYear = `${monthNumber}/${year}`;
        yearStructure[monthYear] = { 1: null, 2: null, 3: null, 4: null };
        attemptsYearStructure[monthYear] = { 1: 0, 2: 0, 3: 0, 4: 0 };
      }
    }
    const monthYear = dateObj.toLocaleString("en-GB", {
      month: "2-digit",
      year: "numeric",
    });
    const week = Math.ceil(dateObj.getDate() / 7);
    const modWeek = week > 4 ? 4 : week;
    // if no performance provided (video/note view etc), populate relevant part of year with data
    if (!performances) {
      yearStructure[monthYear][modWeek]
        ? (yearStructure[monthYear][modWeek]! += 1)
        : (yearStructure[monthYear][modWeek] = 1);
    }
    // otherwise, update total
    else {
      const currentNumbersInAverage = attemptsYearStructure[monthYear][modWeek];
      const currentAverage = yearStructure[monthYear][modWeek];
      yearStructure[monthYear][modWeek]
        ? (yearStructure[monthYear][modWeek]! =
            (currentAverage! * currentNumbersInAverage + performances[indx]) /
            (currentNumbersInAverage + 1))
        : (yearStructure[monthYear][modWeek] = performances[indx]);
      attemptsYearStructure[monthYear][modWeek] += 1;
    }
  });
  const finalData = Object.entries(yearStructure).map(([date, values]) => {
    const entry: GraphMonthData = {
      date: date,
      "Week 1": 0,
      "Week 2": 0,
      "Week 3": 0,
      "Week 4": 0,
      Average: 0,
    };
    let count = 0;
    let total = 0;
    Object.keys(values).forEach((key) => {
      const weekValue = values[Number(key)]
        ? roundTo2DP(values[Number(key)]!)
        : null;
      if (weekValue) {
        count++;
        total += weekValue;
        entry[`Week ${key}` as "Week 1" | "Week 2" | "Week 3" | "Week 4"] =
          weekValue;
      }
    });
    const demoninator = performances ? count : 4;
    if (demoninator !== 0) {
      entry["Average"] = total / demoninator;
    }
    return entry;
  });
  return { attemptsYearStructure, finalData };
}
